import "ag-charts-enterprise";
import { AgCharts } from "ag-charts-react";
import { ComponentProps, useId, useMemo } from "react";
import { chartRoot } from "./Chart.css";
import { useAgChartTheme } from "./hooks/useAgChartTheme";

type AgChartOptions = ComponentProps<typeof AgCharts>["options"];

export interface ChartProps {
  /**
   * Chart configuration options as per AG Charts documentation.
   */
  options: AgChartOptions;
  /**
   * If true, the chart will display a loading overlay.
   */

  isLoading: boolean;
}

export function Chart({ options, isLoading }: ChartProps) {
  const agChartTheme = useAgChartTheme(isLoading);

  const chartId = useId();

  const loadingDataSource = useMemo(
    () => ({ getData: () => new Promise<never>(() => {}) }),
    []
  );

  const chartOptions = useMemo(
    () =>
      ({
        ...options,
        dataSource: isLoading ? loadingDataSource : options.dataSource,
        theme: agChartTheme,
        tooltip: {
          pagination: true,
          ...options.tooltip
        },
        ...(isLoading ? { zoom: undefined } : { zoom: options.zoom }),
        ...(isLoading
          ? { navigator: undefined }
          : { navigator: options.navigator })
      }) satisfies AgChartOptions,
    [options, isLoading, loadingDataSource, agChartTheme]
  );

  /**
   * Using a key to force remount the chart when isLoading changes.
   * This ensures the loading overlay appears correctly.
   */

  const chartKey = `${chartId}-${isLoading ? "loading" : "ready"}`;

  return (
    <div className={chartRoot}>
      <AgCharts options={chartOptions} key={chartKey} />
    </div>
  );
}
